// Route tree + router (B5B §2.3 beforeLoad gate, §3 layout). Code-based routing keeps
// tsc + vite build green headless with no generated artefact (C4.0-D5). PLAN-C4.0 task-2.
import { createRootRouteWithContext, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { hasPermission, type SympllexSession } from "@sympllex/auth";
import { RootLayout } from "./routes/root";
import { DashboardRoute } from "./routes/dashboard";
import { CanonicalLibrariesRoute } from "./routes/canonical-libraries";
import { ConsultancyFirmsRoute } from "./routes/consultancy-firms";
import { ConsultancyFirmDetailRoute } from "./routes/consultancy-firm-detail";
import { BillingOverviewRoute } from "./routes/billing-overview";
import { CounselReviewRoute } from "./routes/counsel";
import { CounselReviewDetailRoute } from "./routes/counsel-detail";
import { ComplianceRoute } from "./routes/compliance";
import { EvalRunsRoute } from "./routes/eval-runs";
import { ForbiddenRoute } from "./routes/forbidden";
import { DEV_SESSION } from "./lib/session";

export interface RouterContext { session: SympllexSession }

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: DashboardRoute });

const canonicalLibrariesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/canonical-libraries",
  beforeLoad: ({ context }) => {
    // UI-affordance gate; the admin-worker re-checks platform_admin server-side.
    if (!hasPermission(context.session, "super_tenant.canonical_library.write")) {
      throw redirect({ to: "/forbidden" });
    }
  },
  component: CanonicalLibrariesRoute,
});

const consultancyFirmsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/consultancy-firms",
  beforeLoad: ({ context }) => {
    // UI-affordance gate; the admin-worker re-checks super_tenant_admin server-side.
    if (!hasPermission(context.session, "super_tenant.canonical_library.write")) {
      throw redirect({ to: "/forbidden" });
    }
  },
  component: ConsultancyFirmsRoute,
});

const consultancyFirmDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/consultancy-firms/$firmId",
  beforeLoad: ({ context }) => {
    if (!hasPermission(context.session, "super_tenant.canonical_library.write")) throw redirect({ to: "/forbidden" });
  },
  component: ConsultancyFirmDetailRoute,
});

const billingOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  beforeLoad: ({ context }) => {
    if (!hasPermission(context.session, "tenant_billing.read")) throw redirect({ to: "/forbidden" });
  },
  component: BillingOverviewRoute,
});

const counselReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/counsel",
  beforeLoad: ({ context }) => {
    if (!hasPermission(context.session, "super_tenant.security_audit.read")) throw redirect({ to: "/forbidden" });
  },
  component: CounselReviewRoute,
});

const counselReviewDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/counsel/$reviewId",
  beforeLoad: ({ context }) => {
    if (!hasPermission(context.session, "super_tenant.security_audit.read")) throw redirect({ to: "/forbidden" });
  },
  component: CounselReviewDetailRoute,
});

const complianceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/compliance",
  beforeLoad: ({ context }) => {
    if (!hasPermission(context.session, "super_tenant.security_audit.read")) throw redirect({ to: "/forbidden" });
  },
  component: ComplianceRoute,
});

const evalRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/eval-runs",
  beforeLoad: ({ context }) => {
    if (!hasPermission(context.session, "super_tenant.security_audit.read")) throw redirect({ to: "/forbidden" });
  },
  component: EvalRunsRoute,
});

const forbiddenRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forbidden", component: ForbiddenRoute });

const routeTree = rootRoute.addChildren([indexRoute, canonicalLibrariesRoute, consultancyFirmsRoute, consultancyFirmDetailRoute, billingOverviewRoute, counselReviewRoute, counselReviewDetailRoute, complianceRoute, evalRunsRoute, forbiddenRoute]);

export const router = createRouter({
  routeTree,
  context: { session: DEV_SESSION },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}
